import {Button, Col, Input, message, Row, Space, Tooltip} from "antd";
import {
  CopyOutlined,
  FacebookFilled,
  MailFilled,
  TwitterOutlined,
} from "@ant-design/icons";
import {useState} from "react";
import {useNavigate} from "react-router-dom";
import {OfferingAutomations} from "../../../../../components/offering-form/automations-step";
import {StyledServiceButtonWrapper} from "../../../../../styles/onboarding/onboarding";
import {useCreateLiveEventContext} from "../../context/create-live-event";
import {CustomModal} from "../../../../../components/modal";
import {REBRAND_BASE_URL} from "../../../../../services/config";
import {StyledModalContentWrapper} from "../../../../../styles/transactions/transactions";
import {StyledSuccessServiceContent} from "../../../../../components/calls/service/service-form/service-form.style";
import {useGetProfileContext} from "../../../../../context/profile/profile-context";
import {handleLiveClassLink} from "../../../../../utils/live-class";
import {
  OFFERING_CREATE_DRAFT_KEYS,
  useClearOfferingCreateDraft,
} from "../../../../../utils/offering-create-draft";

export function CreateLiveEventAutomationsStep() {
  const {
    dispatch,
    state: {apiResponseData},
  } = useCreateLiveEventContext();
  const navigate = useNavigate();
  const {state: profileState} = useGetProfileContext();
  const [openSuccessfulModal, setOpenSuccessfulModal] = useState(false);
  const clearLiveEventDraft = useClearOfferingCreateDraft(
    OFFERING_CREATE_DRAFT_KEYS.liveEvent
  );

  const eventId = apiResponseData?.id;
  const slug = apiResponseData?.slug;
  const tinyUrl = apiResponseData?.tiny_url;
  const eventName = apiResponseData?.name;

  const goBack = () =>
    dispatch({
      type: "update-create-live-event-form-step",
      payload: "cohost",
    });

  const handleShowSuccessModal = () => setOpenSuccessfulModal(true);
  const handleSuccessModalOk = () => {
    setOpenSuccessfulModal(false);
    clearLiveEventDraft();
    navigate("/live-classes");
  };

  return (
    <>
      <Space size={16} direction="vertical" style={{display: "flex"}}>
        <OfferingAutomations
          contextOfferingId={apiResponseData?.id ?? null}
          onPublishSuccess={handleShowSuccessModal}
          renderWizardFooter={() => (
            <Row gutter={[0, 16]}>
              <Col span={24} sm={12}>
                <StyledServiceButtonWrapper>
                  <Button
                    className="secondary-btn"
                    onClick={goBack}
                    block
                    size="small"
                    type="default"
                  >
                    Back
                  </Button>
                  <Button
                    block
                    type="primary"
                    size="small"
                    onClick={handleShowSuccessModal}
                  >
                    Create live event
                  </Button>
                </StyledServiceButtonWrapper>
              </Col>
            </Row>
          )}
        />
      </Space>
      {eventId && eventName && slug && (
        <CustomModal
          visibleState={openSuccessfulModal}
          title="Live event created"
          okFunc={handleSuccessModalOk}
          cancelFunc={handleSuccessModalOk}
          footerContent={[
            <Button
              key="publicProfile"
              type="primary"
              href={`${REBRAND_BASE_URL}/${profileState.profileData.username}`}
              target="_blank"
              size="small"
            >
              View my public profile
            </Button>,
            <Button
              size="small"
              key="gotIt"
              type="default"
              onClick={handleSuccessModalOk}
            >
              Got it!
            </Button>,
          ]}
        >
          <StyledModalContentWrapper>
            <StyledSuccessServiceContent>
              <p className="success-text">
                Your live event has been successfully created! Share your event link to
                start getting bookings
              </p>
              <div className="success-share-info">
                <Input.Group compact style={{display: "flex"}}>
                  <Input
                    size="large"
                    readOnly
                    defaultValue={handleLiveClassLink({
                      eventId: eventId,
                      slug: slug,
                      username: profileState.profileData.username,
                      tinyUrl: tinyUrl,
                      windowNav: true,
                    })}
                  />
                  <Tooltip title={"Copy event url"}>
                    <Button
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() =>
                        navigator.clipboard
                          .writeText(
                            handleLiveClassLink({
                              eventId: eventId,
                              slug: slug,
                              tinyUrl: tinyUrl,
                              username: profileState.profileData.username,
                              windowNav: true,
                            })
                          )
                          .then(() => message.success("Copied"))
                      }
                    />
                  </Tooltip>
                </Input.Group>
                <p>or</p>
                <Space direction="horizontal" size={16}>
                  <div>
                    <Button
                      shape="circle"
                      size="small"
                      icon={<FacebookFilled />}
                      style={{
                        backgroundColor: "#EBEEF5",
                        color: "#3B5998",
                        border: "none",
                      }}
                      type="link"
                      target="_blank"
                      href={`https://www.facebook.com/sharer/sharer.php?u=${handleLiveClassLink(
                        {
                          eventId: eventId,
                          slug: slug,
                          tinyUrl: tinyUrl,
                          username: profileState.profileData.username,
                          windowNav: true,
                        }
                      )}&amp`}
                      title="Share on facebook"
                    />
                  </div>
                  <div>
                    <Button
                      title="Share on twitter"
                      shape="circle"
                      size="small"
                      icon={<TwitterOutlined />}
                      target="_blank"
                      style={{
                        backgroundColor: "#E6F7FD",
                        color: "#00ACEE",
                        border: "none",
                      }}
                      href={`https://twitter.com/intent/tweet?text=I just created a live event called "${eventName}" on Coachli. Book a live event with me today on Coachli!&url=${handleLiveClassLink(
                        {
                          eventId: eventId,
                          slug: slug,
                          tinyUrl: tinyUrl,
                          username: profileState.profileData.username,
                        }
                      )}&hashtags=coachli,useCoachli`}
                    />
                  </div>
                  <div>
                    <Button
                      title="Send mail"
                      shape="circle"
                      size="small"
                      icon={<MailFilled />}
                      style={{
                        backgroundColor: "#FBECEB",
                        color: "#DB4437",
                        border: "none",
                      }}
                      href={`mailto:?subject=I wanted you to see this site&amp;body=Book a live event with me on Coachli ${handleLiveClassLink(
                        {
                          eventId: eventId,
                          slug: slug,
                          tinyUrl: tinyUrl,
                          username: profileState.profileData.username,
                          windowNav: true,
                        }
                      )}`}
                    />
                  </div>
                </Space>
              </div>
            </StyledSuccessServiceContent>
          </StyledModalContentWrapper>
        </CustomModal>
      )}
    </>
  );
}
